/**
 * Enterprise Workspace Module Catalog — Frontend
 *
 * Single source of truth for:
 *   MODULE_CATALOG       — full metadata for all displayable modules (44 modules, 7 categories)
 *   MODULE_CATEGORIES    — ordered category list for the Modules management page
 *   MODULE_ID_TO_BACKEND — sidebar route-id → backend module name (for sidebar gating)
 *   WS_MODULE_ROUTE_MAP  — route-id → backend module name (for App.jsx route guards, wider coverage)
 *
 * Backend module names MUST match VALID_WORKSPACE_MODULES in
 * crm-backend/src/modules/tenancy/workspace/dto/workspace-module-catalog.constant.ts.
 *
 * Industry vertical modules (BFSI, Healthcare, etc.) are not shown in the default
 * Modules UI but are valid workspace.modules[] values — they are gated per-drawer
 * by their own store.modules checks.
 */

export const MODULE_CATEGORIES = [
  'CRM Core',
  'Sales',
  'Engagement',
  'AI',
  'Insights',
  'Platform',
  'Administration',
];

/**
 * Full displayable module catalog.
 * id           — backend module name (stored in workspace.modules[])
 * routeId      — CRM sidebar / route id
 * label        — display name
 * icon         — NexusIcon name
 * category     — one of MODULE_CATEGORIES
 * desc         — one-line description shown in Modules management page
 * deps         — backend module ids this module depends on (informational only)
 * configSchema — M-07: placeholder for future per-module configuration schema (null = not configured)
 */
export const MODULE_CATALOG = [
  // ── CRM Core ───────────────────────────────────────────────────
  {
    id: 'People',     routeId: 'people',    label: 'People',    icon: 'User',
    category: 'CRM Core',
    desc: 'Contact management, person profiles, and relationship tracking.',
    deps: [], configSchema: null,
  },
  {
    id: 'Companies',  routeId: 'companies', label: 'Companies', icon: 'Building2',
    category: 'CRM Core',
    desc: 'Company profiles, firmographics, and account management.',
    deps: [], configSchema: null,
  },
  {
    id: 'Pipeline',   routeId: 'deals',     label: 'Pipeline',  icon: 'Target',
    category: 'CRM Core',
    desc: 'Deal management, pipeline stages, and revenue tracking.',
    deps: ['People'], configSchema: null,
  },
  {
    id: 'Tasks',      routeId: 'tasks',     label: 'Tasks',     icon: 'CheckSquare',
    category: 'CRM Core',
    desc: 'Task management, assignments, and due-date tracking.',
    deps: [], configSchema: null,
  },
  {
    id: 'Calendar',   routeId: 'calendar',  label: 'Calendar',  icon: 'Calendar',
    category: 'CRM Core',
    desc: 'Calendar view for tasks, meetings, and scheduled events.',
    deps: [], configSchema: null,
  },
  {
    id: 'Notes',      routeId: 'notes',     label: 'Notes',     icon: 'StickyNote',
    category: 'CRM Core',
    desc: 'Note-taking and knowledge capture across all CRM records.',
    deps: [], configSchema: null,
  },

  // ── Sales ──────────────────────────────────────────────────────
  {
    id: 'SalesWorkspace',       routeId: 'sales',         label: 'Sales Workspace',       icon: 'Briefcase',
    category: 'Sales',
    desc: 'Consolidated sales workspace with pipeline, quota, and forecast views.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'Quotes',               routeId: 'quotes',        label: 'Quotes',                icon: 'FileText',
    category: 'Sales',
    desc: 'Quote generation, versioning, and approval workflows.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'Products',             routeId: 'products',      label: 'Products',              icon: 'Package',
    category: 'Sales',
    desc: 'Product catalog with pricing, variants, and SKU management.',
    deps: [], configSchema: null,
  },
  {
    id: 'PriceBooks',           routeId: 'price-books',   label: 'Price Books',           icon: 'BookOpen',
    category: 'Sales',
    desc: 'Custom pricing tiers for different customer segments and regions.',
    deps: ['Products'], configSchema: null,
  },
  {
    id: 'Approvals',            routeId: 'approvals',     label: 'Approvals',             icon: 'ClipboardList',
    category: 'Sales',
    desc: 'Structured approval workflows for deals, quotes, and discounts.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'Forecast',             routeId: 'forecast',      label: 'Forecast',              icon: 'TrendingUp',
    category: 'Sales',
    desc: 'Revenue forecasting, attainment tracking, and scenario planning.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'Goals',                routeId: 'goals',         label: 'Goals',                 icon: 'Award',
    category: 'Sales',
    desc: 'Sales goal setting, tracking, and attainment reporting.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'Territory',            routeId: 'territory',     label: 'Territory',             icon: 'MapPin',
    category: 'Sales',
    desc: 'Territory management, team alignment, and account segmentation.',
    deps: ['People'], configSchema: null,
  },
  {
    id: 'PipelineIntelligence', routeId: 'intelligence',  label: 'Pipeline Intelligence', icon: 'Activity',
    category: 'Sales',
    desc: 'AI-powered deal risk scoring, anomaly detection, and close probability.',
    deps: ['Pipeline'], configSchema: null,
  },

  // ── Engagement ─────────────────────────────────────────────────
  {
    id: 'Email',          routeId: 'emails',        label: 'Email',              icon: 'Mail',
    category: 'Engagement',
    desc: 'Email inbox, send, track opens and clicks across contacts.',
    deps: [], configSchema: null,
  },
  {
    id: 'Meetings',       routeId: 'meetings',      label: 'Meetings',           icon: 'Video',
    category: 'Engagement',
    desc: 'Meeting scheduling, video links, and calendar sync.',
    deps: [], configSchema: null,
  },
  {
    id: 'Activities',     routeId: 'activities',    label: 'Activities',         icon: 'Activity',
    category: 'Engagement',
    desc: 'Call logs, activity tracking, and engagement history.',
    deps: [], configSchema: null,
  },
  {
    id: 'Timeline',       routeId: 'timeline',      label: 'Customer Timeline',  icon: 'Clock',
    category: 'Engagement',
    desc: 'Chronological view of all customer interactions and milestones.',
    deps: [], configSchema: null,
  },
  {
    id: 'Collaboration',  routeId: 'collaboration', label: 'Collaboration',      icon: 'MessageSquare',
    category: 'Engagement',
    desc: 'Team mentions, comments, and internal communication threads.',
    deps: [], configSchema: null,
  },
  {
    id: 'Notifications',  routeId: 'notifications', label: 'Notifications',      icon: 'Bell',
    category: 'Engagement',
    desc: 'In-app notification center for alerts, mentions, and task updates.',
    deps: [], configSchema: null,
  },
  {
    id: 'Workflows',      routeId: 'workflows',     label: 'Workflows',          icon: 'Zap',
    category: 'Engagement',
    desc: 'Automated process workflows triggered by CRM events and conditions.',
    deps: [], configSchema: null,
  },
  {
    id: 'Customer360',    routeId: 'customer-360',  label: 'Customer 360',       icon: 'Users',
    category: 'Engagement',
    desc: '360° customer view aggregating all touchpoints, history, and health.',
    deps: ['People'], configSchema: null,
  },

  // ── AI ─────────────────────────────────────────────────────────
  {
    id: 'AIAssistant',     routeId: 'copilot',           label: 'AI Assistant',      icon: 'Star',
    category: 'AI',
    desc: 'Conversational AI copilot for CRM data insights and automation.',
    deps: [], configSchema: null,
  },
  {
    id: 'AIGateway',       routeId: 'llm-gateway',       label: 'AI Gateway',        icon: 'Cpu',
    category: 'AI',
    desc: 'Centralized LLM routing, model selection, and usage governance.',
    deps: ['AIAssistant'], configSchema: null,
  },
  {
    id: 'PromptTemplates', routeId: 'prompt-templates',  label: 'Prompt Templates',  icon: 'FileEdit',
    category: 'AI',
    desc: 'Reusable prompt library for consistent AI-assisted workflows.',
    deps: ['AIAssistant'], configSchema: null,
  },
  {
    id: 'AISessions',      routeId: 'ai-sessions',       label: 'AI Sessions',       icon: 'Database',
    category: 'AI',
    desc: 'Session history, token usage, and AI interaction logs.',
    deps: ['AIAssistant'], configSchema: null,
  },
  {
    id: 'MLScores',        routeId: 'ml-scores',         label: 'ML Scores',         icon: 'Gauge',
    category: 'AI',
    desc: 'Machine learning model scores for lead quality, churn, and engagement.',
    deps: [], configSchema: null,
  },
  {
    id: 'HealthScore',     routeId: 'health-score',      label: 'Health Score',      icon: 'HeartPulse',
    category: 'AI',
    desc: 'Account health scoring based on engagement, product usage, and sentiment.',
    deps: ['MLScores'], configSchema: null,
  },

  // ── Insights ───────────────────────────────────────────────────
  {
    id: 'Reports',          routeId: 'reports',           label: 'Reports',           icon: 'FileText',
    category: 'Insights',
    desc: 'Custom report builder with drag-and-drop analytics and export.',
    deps: [], configSchema: null,
  },
  {
    id: 'Dashboards',       routeId: 'dashboards',        label: 'Dashboards',        icon: 'LayoutDashboard',
    category: 'Insights',
    desc: 'Configurable dashboards with KPIs, charts, and real-time tiles.',
    deps: [], configSchema: null,
  },
  {
    id: 'SavedSearches',    routeId: 'saved-searches',    label: 'Saved Searches',    icon: 'Search',
    category: 'Insights',
    desc: 'Saved filter sets and search configurations for quick access.',
    deps: [], configSchema: null,
  },
  {
    id: 'RevenueAnalytics', routeId: 'revenue-analytics', label: 'Revenue Analytics', icon: 'DollarSign',
    category: 'Insights',
    desc: 'Executive revenue dashboards, ARR trends, and cohort analysis.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'LiveFeed',         routeId: 'realtime',          label: 'Live Feed',         icon: 'Radio',
    category: 'Insights',
    desc: 'Real-time activity stream of CRM events and user actions.',
    deps: [], configSchema: null,
  },
  {
    id: 'Analytics',        routeId: 'analytics',         label: 'Analytics',         icon: 'BarChart2',
    category: 'Insights',
    desc: 'Pipeline analytics, conversion rates, and team performance metrics.',
    deps: ['Pipeline'], configSchema: null,
  },
  {
    id: 'CommAnalytics',    routeId: 'comm-analytics',    label: 'Communication Analytics', icon: 'MessageCircle',
    category: 'Insights',
    desc: 'Email, call, and meeting analytics with outreach effectiveness metrics.',
    deps: ['Email'], configSchema: null,
  },

  // ── Platform ───────────────────────────────────────────────────
  {
    id: 'Webhooks',      routeId: 'webhooks',    label: 'Webhooks',      icon: 'Webhook',
    category: 'Platform',
    desc: 'Outbound webhooks for real-time event delivery to external systems.',
    deps: [], configSchema: null,
  },
  {
    id: 'Integrations',  routeId: 'integration', label: 'Integrations',  icon: 'Plug',
    category: 'Platform',
    desc: 'Third-party integration hub for connecting external tools and APIs.',
    deps: [], configSchema: null,
  },
  {
    id: 'Plugins',       routeId: 'plugins',     label: 'Plugins',       icon: 'Layers',
    category: 'Platform',
    desc: 'Extensibility platform for custom plugins and third-party apps.',
    deps: [], configSchema: null,
  },
  {
    id: 'Jobs',          routeId: 'jobs',        label: 'Jobs',          icon: 'HardDrive',
    category: 'Platform',
    desc: 'Background job management, scheduler, and execution monitoring.',
    deps: [], configSchema: null,
  },

  // ── Administration ─────────────────────────────────────────────
  {
    id: 'Forms',         routeId: 'forms',          label: 'Forms',           icon: 'ClipboardList',
    category: 'Administration',
    desc: 'Web form builder for lead capture, surveys, and data collection.',
    deps: [], configSchema: null,
  },
  {
    id: 'ImportExport',  routeId: 'import-export',  label: 'Import / Export', icon: 'RefreshCw',
    category: 'Administration',
    desc: 'Bulk data import and export with field mapping and validation.',
    deps: [], configSchema: null,
  },
  {
    id: 'CustomObjects', routeId: 'custom-objects', label: 'Custom Objects',  icon: 'Database',
    category: 'Administration',
    desc: 'Define and manage custom data objects and relationships in the CRM.',
    deps: [], configSchema: null,
  },
  {
    id: 'Automation',    routeId: 'automation',     label: 'Automation',      icon: 'GitBranch',
    category: 'Administration',
    desc: 'Business process automation with visual workflow designer.',
    deps: [], configSchema: null,
  },
];

/**
 * Sidebar route-id → backend module name.
 * Used by layout/index.jsx to filter each sidebar section.
 * Keys are sidebar item `id` values; values are workspace.modules[] backend names.
 */
export const MODULE_ID_TO_BACKEND = {
  // CRM Core (sidebar: people, companies, tasks, notes — calendar has no sidebar item)
  people: 'People', companies: 'Companies', tasks: 'Tasks', notes: 'Notes',
  // Sales
  sales: 'SalesWorkspace', deals: 'Pipeline', pipeline: 'Pipeline',
  forecast: 'Forecast', intelligence: 'PipelineIntelligence', analytics: 'Analytics',
  quotes: 'Quotes', products: 'Products', 'price-books': 'PriceBooks',
  approvals: 'Approvals', goals: 'Goals', territory: 'Territory',
  // Engagement
  emails: 'Email', meetings: 'Meetings', activities: 'Activities',
  timeline: 'Timeline', collaboration: 'Collaboration', notifications: 'Notifications',
  workflows: 'Workflows', 'customer-360': 'Customer360',
  // AI
  copilot: 'AIAssistant', 'llm-gateway': 'AIGateway', 'prompt-templates': 'PromptTemplates',
  'ai-sessions': 'AISessions', 'ml-scores': 'MLScores', 'health-score': 'HealthScore',
  // Insights
  reports: 'Reports', dashboards: 'Dashboards', 'saved-searches': 'SavedSearches',
  'revenue-analytics': 'RevenueAnalytics', realtime: 'LiveFeed',
  // Platform
  webhooks: 'Webhooks', integration: 'Integrations', plugins: 'Plugins', jobs: 'Jobs',
  // Administration
  forms: 'Forms', 'import-export': 'ImportExport',
  'custom-objects': 'CustomObjects', automation: 'Automation',
};

/**
 * Route-id → backend module name for App.jsx route guards.
 * Wider than MODULE_ID_TO_BACKEND: covers routes that exist in PATH_TO_MODULE
 * but have no corresponding sidebar item (exec-dashboard, commerce, etc.).
 */
export const WS_MODULE_ROUTE_MAP = {
  ...MODULE_ID_TO_BACKEND,
  // CRM Core route-only (no sidebar entry for calendar)
  calendar: 'Calendar',
  // Extra App.jsx route IDs not in sidebar
  'comm-analytics': 'CommAnalytics',
  'exec-dashboard': 'RevenueAnalytics',
  commerce:         'Products',
  predictive:       'MLScores',
  monitoring:       'Analytics',
};

/**
 * Default modules enabled for new workspaces (mirrors backend DEFAULT_WORKSPACE_MODULES).
 * Used by the create-workspace modal to pre-check core modules.
 */
export const DEFAULT_MODULES = ['People', 'Pipeline', 'Companies', 'Tasks', 'Calendar', 'Notes'];
